import { useEffect, useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import {
  Activity,
  Bell,
  BellPlus,
  BookOpen,
  ClipboardCheck,
  FileText,
  KeyRound,
  Map as MapIcon,
  MapPin,
  Menu,
  Navigation,
  Cpu,
  Receipt,
  Route,
  Settings as SettingsIcon,
  Ship,
  ShieldCheck,
  Siren,
  Wallet,
  Waves,
  Wrench,
} from 'lucide-react';
import { ThemeToggle } from './ThemeToggle';
import { HeaderStats } from './HeaderStats';
import { WeatherWidget } from './WeatherWidget';
import { HeaderProfile } from './HeaderProfile';
import { cn } from '@/lib/utils';
import { useFleet } from '@/hooks/useFleet';
import { useTraccarSocket } from '@/hooks/useTraccarSocket';
import { useGeofenceAutoBlock } from '@/hooks/useGeofenceAutoBlock';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { useEventsCollector } from '@/hooks/useEventsCollector';
import { useCommandResultListener } from '@/hooks/useCommandResultListener';
import { useEventsStore } from '@/stores/events';
import { useConditionsRefresh } from '@/hooks/useConditions';
import { NotificationToggle } from './NotificationToggle';
import { HeaderNotifications } from './HeaderNotifications';
import { SoundToggle } from './SoundToggle';
import { meApi } from '@/api/endpoints/me';
import { useEntitlements, hexToHslTriplet } from '@/stores/entitlements';
import { useDerivedAlerts } from '@/hooks/useDerivedAlerts';

// `mod` = chave do módulo (gate por entitlements). Itens sem `mod` são sempre exibidos.
const primaryNav = [
  { to: '/devices', label: 'Frota', icon: Ship },
  { to: '/map', label: 'Mapa Náutico', icon: MapIcon, mod: 'mapa' },
  { to: '/dashboard', label: 'Painel', icon: Activity },
];

const navGroups = [
  {
    title: 'Navegação',
    items: [
      { to: '/conditions', label: 'Condições do Mar', icon: Waves, mod: 'condicoes' },
      { to: '/points', label: 'Pontos Náuticos', icon: MapPin, mod: 'pontos' },
      { to: '/navigator', label: 'Navegador Premium', icon: Navigation, mod: 'navegador' },
      { to: '/app-routes', label: 'Rotas do App', icon: Route, mod: 'rotas_app' },
    ],
  },
  {
    title: 'Operação',
    items: [
      { to: '/geofences', label: 'Geocercas', icon: ShieldCheck, mod: 'geocercas' },
      { to: '/notifications', label: 'Alertas', icon: Bell, mod: 'alertas' },
      // Admin e revenda. O Traccar exige administrador para gravar o atributo computado (a
      // condição), então a escrita passa pelo backend em `/geo/v1/custom-alarms`, que confere
      // escopo e formato da expressão antes de assinar com a sessão administrativa.
      { to: '/notifications/custom', label: 'Alarmes Personalizados', icon: BellPlus, roles: ['admin', 'revenda'] },
      { to: '/central', label: 'Central 24h', icon: Siren, mod: 'central24h' },
      { to: '/maintenance', label: 'Manutenção', icon: Wrench, mod: 'manutencao' },
      { to: '/checklist', label: 'Checklist', icon: ClipboardCheck, mod: 'checklist' },
      { to: '/rentals', label: 'Locadora', icon: KeyRound, mod: 'locadora' },
    ],
  },
  {
    title: 'Gestão',
    items: [
      { to: '/reports', label: 'Relatórios', icon: FileText, mod: 'relatorios' },
      { to: '/billing', label: 'Cobrança', icon: Receipt, roles: ['admin', 'revenda', 'operador_financeiro', 'cliente'] },
      { to: '/manuais', label: 'Manuais', icon: BookOpen },
      { to: '/settings', label: 'Configurações', icon: SettingsIcon },
    ],
  },
  {
    // `role` = visível conforme papel (admin/revenda). Não usa gate de módulo.
    // Painel Executivo → aba dentro de Locadora · Operadores → aba dentro de Usuários
    // Revendas & cadastro de Clientes → dentro de Configurações · Planos removido.
    title: 'Administração',
    items: [
      { to: '/financeiro', label: 'Financeiro', icon: Wallet, roles: ['admin', 'revenda', 'operador_financeiro'] },
      { to: '/chips', label: 'Chips / SIM', icon: Cpu, roles: ['admin', 'revenda', 'operador_suporte'] },
      { to: '/install', label: 'Ativação Técnica', icon: Wrench, roles: ['admin', 'operador_suporte', 'tecnico'] },
      { to: '/admin/feature-usage', label: 'Uso de Features', icon: Receipt, roles: ['admin'] },
      { to: '/admin/info', label: 'Info / Updates', icon: FileText, roles: ['admin', 'revenda'] },
    ],
  },
];

/** Faixa de aviso quando a conta (tenant) está suspensa/inadimplente. */
function TenantStatusBanner() {
  const status = useEntitlements((s) => s.me?.tenant?.status ?? null);
  if (!status || status === 'ativo') return null;
  const inadimplente = status === 'inadimplente';
  return (
    <div
      className={cn(
        'flex items-center justify-center gap-2 px-3 py-1.5 text-center text-xs font-semibold print:hidden',
        inadimplente
          ? 'bg-status-ready/15 text-status-ready'
          : 'bg-status-blocked/15 text-status-blocked',
      )}
    >
      <Siren className="h-3.5 w-3.5 shrink-0" />
      {inadimplente
        ? 'Conta inadimplente — regularize o pagamento para evitar o bloqueio. Novos registros estão suspensos.'
        : 'Conta suspensa — entre em contato com o suporte. Gravações bloqueadas.'}
    </div>
  );
}

/** Item visível? `role`/`roles` → confere papel; `mod` → confere módulo habilitado; senão sempre. */
type NavGate = { mod?: string; role?: string; roles?: string[] };
function isNavVisible(item: NavGate, isEnabled: (k: string) => boolean, papel: string | null): boolean {
  if (item.roles) return !!papel && item.roles.includes(papel);
  if (item.role) return papel === item.role;
  if (item.mod) return isEnabled(item.mod);
  return true;
}

const ASSET = import.meta.env.BASE_URL; // '/v1/'
const LOGO_HORIZONTAL = `${ASSET}brand/nautmax-logo.png`;
const LOGO_MARK = `${ASSET}brand/icon-512.png`;

/** Marca quadrada: logo do tenant (white-label) ou o símbolo NautMax padrão. */
function BrandMark({ className }: { className?: string }) {
  const logoUrl = useEntitlements((s) => s.me?.branding.logo_url ?? null);
  return (
    <img
      src={logoUrl ?? LOGO_MARK}
      alt="Logo"
      className={cn('rounded-xl object-contain', className)}
    />
  );
}

/** Há branding customizado de revenda/cliente? (senão, marca NautMax padrão) */
function useIsCustomBrand() {
  return useEntitlements(
    (s) => !!s.me && (!!s.me.branding.logo_url || s.me.branding.nome_exibicao !== 'NautMax'),
  );
}

/**
 * Logo "da esquerda": no padrão NautMax exibe a logo horizontal completa;
 * com white-label exibe a marca do tenant + nome de exibição.
 */
function BrandLockup({
  imgClass,
  markClass,
  textClass,
}: {
  imgClass: string;
  markClass: string;
  textClass?: string;
}) {
  const custom = useIsCustomBrand();
  const name = useEntitlements((s) => s.me?.branding.nome_exibicao ?? 'NautMax');
  if (custom) {
    return (
      <>
        <BrandMark className={markClass} />
        <span className={cn('font-bold tracking-tight', textClass)}>{name}</span>
      </>
    );
  }
  return (
    <img src={LOGO_HORIZONTAL} alt="NautMax" className={cn('w-auto object-contain', imgClass)} />
  );
}

/** Aplica branding do tenant às CSS vars + título + favicon. */
function applyBranding(b: { nome_exibicao: string; cor_marca: string; cor_primaria: string; favicon_url: string | null }) {
  document.title = b.nome_exibicao || 'NautMax';
  const root = document.documentElement;
  const brand = hexToHslTriplet(b.cor_marca);
  const primary = hexToHslTriplet(b.cor_primaria);
  if (brand) root.style.setProperty('--brand', brand);
  if (primary) {
    root.style.setProperty('--primary', primary);
    root.style.setProperty('--ring', primary);
  }
  if (b.favicon_url) {
    let link = document.querySelector<HTMLLinkElement>("link[rel='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = b.favicon_url;
  }
}

export function AppLayout() {
  useFleet();
  useTraccarSocket();
  useGeofenceAutoBlock();
  usePushNotifications();
  useEventsCollector();
  useCommandResultListener();
  useConditionsRefresh();
  useDerivedAlerts();

  const [sidebarHidden, setSidebarHidden] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const unreadCount = useEventsStore((s) => s.items.filter((e) => !e.read).length);
  const isEnabled = useEntitlements((s) => s.isEnabled);
  const setMe = useEntitlements((s) => s.setMe);
  const papel = useEntitlements((s) => s.me?.user.papel ?? null);

  // Carrega entitlements/branding do tenant logo após o login (sessão Traccar).
  useEffect(() => {
    let alive = true;
    meApi
      .get()
      .then((m) => {
        if (!alive) return;
        setMe(m);
        applyBranding(m.branding);
      })
      .catch(() => {
        /* sem sessão ainda / offline — mantém defaults NautMax */
      });
    return () => {
      alive = false;
    };
  }, [setMe]);

  // Gate por módulo habilitado (mod) ou papel (role); itens sem ambos são core.
  const visiblePrimary = primaryNav.filter((i) => isNavVisible(i as NavGate, isEnabled, papel));
  const visibleGroups = navGroups
    .map((g) => ({ ...g, items: g.items.filter((i) => isNavVisible(i as NavGate, isEnabled, papel)) }))
    .filter((g) => g.items.length > 0);

  return (
    <div className="flex h-full">
      <aside
        className={cn(
          'shrink-0 flex-col border-r border-border bg-card transition-all duration-200 print:hidden',
          sidebarHidden ? 'hidden lg:hidden' : 'hidden w-60 lg:flex',
        )}
      >
        <div className="flex h-16 items-center gap-3 px-4">
          <BrandLockup imgClass="h-9" markClass="h-10 w-10" textClass="text-lg" />
        </div>
        <nav className="flex-1 space-y-5 overflow-y-auto p-3">
          <div className="space-y-1">
            {visiblePrimary.map((item) => (
              <NavItem key={item.to} {...item} />
            ))}
          </div>
          {visibleGroups.map((g) => (
            <div key={g.title} className="space-y-1">
              <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {g.title}
              </p>
              {g.items.map((item) => (
                <NavItem
                  key={item.to}
                  {...item}
                  badge={item.to === '/notifications' ? unreadCount : undefined}
                />
              ))}
            </div>
          ))}
        </nav>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <TenantStatusBanner />
        <header className="relative flex h-20 items-center justify-between gap-3 border-b border-border bg-card px-3 lg:px-4 print:hidden">
          <div className="flex flex-1 items-center gap-3">
            <button
              type="button"
              onClick={() => setSidebarHidden((v) => !v)}
              className="hidden h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground lg:inline-flex"
              title={sidebarHidden ? 'Mostrar menu' : 'Esconder menu'}
              aria-label="Alternar menu lateral"
            >
              <Menu className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-2 lg:hidden">
              <BrandLockup imgClass="h-7" markClass="h-8 w-8" textClass="text-lg" />
            </div>
            <div className="hidden lg:block xl:hidden">
              <HeaderStats dense />
            </div>
            <div className="hidden xl:block">
              <HeaderStats />
            </div>
          </div>

          {sidebarHidden && (
            <div className="pointer-events-none absolute left-1/2 top-1/2 z-10 hidden -translate-x-1/2 -translate-y-1/2 items-center gap-2 lg:flex">
              <BrandLockup imgClass="h-10" markClass="h-10 w-10" textClass="text-2xl" />
            </div>
          )}

          <div className="flex items-center gap-2">
            <HeaderNotifications />
            <SoundToggle />
            <NotificationToggle />
            <WeatherWidget />
            <HeaderProfile />
            <ThemeToggle />
          </div>
        </header>
        <main className="flex-1 overflow-auto pb-20 lg:pb-0 print:overflow-visible print:pb-0">
          <Outlet />
        </main>
        {menuOpen && <MobileMenu unread={unreadCount} onClose={() => setMenuOpen(false)} />}
        <MobileBottomNav unread={unreadCount} onMore={() => setMenuOpen(true)} />
      </div>
    </div>
  );
}

function MobileMenu({ unread, onClose }: { unread: number; onClose: () => void }) {
  const isEnabled = useEntitlements((s) => s.isEnabled);
  const papel = useEntitlements((s) => s.me?.user.papel ?? null);
  const mPrimary = primaryNav.filter((i) => isNavVisible(i as NavGate, isEnabled, papel));
  const mGroups = navGroups
    .map((g) => ({ ...g, items: g.items.filter((i) => isNavVisible(i as NavGate, isEnabled, papel)) }))
    .filter((g) => g.items.length > 0);
  return (
    <div className="fixed inset-0 z-40 flex flex-col bg-background lg:hidden">
      <div className="flex h-14 items-center justify-between border-b border-border px-4">
        <span className="text-base font-bold">Menu</span>
        <button type="button" onClick={onClose} className="rounded-md p-1.5 text-muted-foreground hover:bg-accent" aria-label="Fechar">
          <Menu className="h-5 w-5" />
        </button>
      </div>
      <nav className="flex-1 space-y-5 overflow-y-auto p-3 pb-24">
        <div className="space-y-1">
          {mPrimary.map((item) => (
            <NavLink key={item.to} to={item.to} onClick={onClose}
              className={({ isActive }) => cn('flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium', isActive ? 'bg-primary text-primary-foreground' : 'hover:bg-accent')}>
              <item.icon className="h-4 w-4" /> {item.label}
            </NavLink>
          ))}
        </div>
        {mGroups.map((g) => (
          <div key={g.title} className="space-y-1">
            <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{g.title}</p>
            {g.items.map((item) => (
              <NavLink key={item.to} to={item.to} onClick={onClose}
                className={({ isActive }) => cn('flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium', isActive ? 'bg-primary text-primary-foreground' : 'hover:bg-accent')}>
                <item.icon className="h-4 w-4" />
                <span className="flex-1">{item.label}</span>
                {item.to === '/notifications' && unread > 0 && (
                  <span className="rounded-full bg-status-blocked px-1.5 text-[10px] font-bold text-destructive-foreground">{unread > 99 ? '99+' : unread}</span>
                )}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>
    </div>
  );
}

function NavItem({
  to,
  label,
  icon: Icon,
  badge,
}: {
  to: string;
  label: string;
  icon: typeof Ship;
  badge?: number;
  mod?: string;
  role?: string;
  roles?: string[];
}) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
          isActive
            ? 'bg-primary text-primary-foreground'
            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
        )
      }
    >
      <Icon className="h-4 w-4" />
      <span className="flex-1">{label}</span>
      {badge && badge > 0 ? (
        <span className="rounded-full bg-status-blocked px-1.5 text-[10px] font-bold text-destructive-foreground">
          {badge > 99 ? '99+' : badge}
        </span>
      ) : null}
    </NavLink>
  );
}

function MobileBottomNav({ unread, onMore }: { unread: number; onMore: () => void }) {
  const items = [
    { to: '/map', label: 'Mapa', icon: MapIcon },
    { to: '/devices', label: 'Frota', icon: Ship },
    { to: '/notifications', label: 'Alertas', icon: Bell },
  ];
  return (
    <nav className="fixed bottom-0 left-0 right-0 z-30 grid grid-cols-4 border-t border-border bg-card lg:hidden print:hidden">
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          className={({ isActive }) =>
            cn(
              'relative flex flex-col items-center justify-center gap-1 py-2 text-[11px] font-medium transition-colors',
              isActive ? 'text-primary' : 'text-muted-foreground',
            )
          }
        >
          <item.icon className="h-5 w-5" />
          {item.label}
          {item.to === '/notifications' && unread > 0 && (
            <span className="absolute right-1/4 top-1 h-1.5 w-1.5 rounded-full bg-status-blocked" />
          )}
        </NavLink>
      ))}
      <button
        type="button"
        onClick={onMore}
        className="flex flex-col items-center justify-center gap-1 py-2 text-[11px] font-medium text-muted-foreground"
      >
        <Menu className="h-5 w-5" />
        Mais
      </button>
    </nav>
  );
}
